import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, IconButton, Tooltip, CircularProgress, Alert, Snackbar,
  Stack, Breadcrumbs, Link,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';

const ROLE_COLOR = { admin: 'error', manager: 'warning', staff: 'primary', viewer: 'default' };

export default function TenantUsersPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [tenantName, setTenantName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, tenantData] = await Promise.all([
        adminApi.listTenantUsers(tenantId),
        adminApi.getTenant(tenantId),
      ]);
      setUsers(usersData.users || []);
      setTenantName(tenantData.tenant?.name || tenantData.name || tenantId);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (userId, enabled) => {
    try {
      if (enabled) {
        await adminApi.disableUser(tenantId, userId);
        setToast({ open: true, message: 'User disabled.', severity: 'info' });
      } else {
        // re-enable — if admin API supports it
        setToast({ open: true, message: 'Re-enable via Cognito console.', severity: 'warning' });
        return;
      }
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton size="small" onClick={() => navigate(`/tenants/${tenantId}`)}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Breadcrumbs>
          <Link component="button" variant="body2" onClick={() => navigate('/tenants')}>Tenants</Link>
          <Link component="button" variant="body2" onClick={() => navigate(`/tenants/${tenantId}`)}>{tenantName}</Link>
          <Typography variant="body2" color="text.primary">Users</Typography>
        </Breadcrumbs>
      </Stack>

      <Typography variant="h5" fontWeight={700} mb={2}>Users — {tenantName}</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No users found.</TableCell></TableRow>
            ) : users.map((u) => (
              <TableRow key={u.userId} hover>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Chip label={u.role} size="small" color={ROLE_COLOR[u.role] || 'default'} variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={u.enabled ? 'Enabled' : 'Disabled'}
                    size="small"
                    color={u.enabled ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={u.enabled ? 'Disable user' : 'Enable user'}>
                    <IconButton size="small" onClick={() => handleToggle(u.userId, u.enabled)}>
                      {u.enabled
                        ? <BlockIcon fontSize="small" color="error" />
                        : <CheckCircleIcon fontSize="small" color="success" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar open={toast.open} autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
